/**
 * Content Panel (Variables)
 * Manages template variables - both built-in and custom
 */

import React, { useState } from 'react';
import { Plus, Trash2, Type, Image, Palette, ToggleLeft } from 'lucide-react';
import { useTemplateStore } from '@/stores';
import type { TemplateVariable, VariableType } from '@/types/template';
import { nanoid } from 'nanoid';

const VARIABLE_ICONS: Record<VariableType, React.ElementType> = {
  text: Type,
  image: Image,
  color: Palette,
  boolean: ToggleLeft,
};

export function ContentPanel() {
  const {
    currentTemplate,
    variableValues,
    setVariableValue,
    addVariable,
    updateVariable,
    deleteVariable,
  } = useTemplateStore();

  const [isAddingVariable, setIsAddingVariable] = useState(false);
  const [newVarName, setNewVarName] = useState('');
  const [newVarType, setNewVarType] = useState<VariableType>('text');

  if (!currentTemplate) {
    return (
      <div className="text-center text-fg-subtle py-8">
        <p>No template loaded</p>
      </div>
    );
  }

  const variables = currentTemplate.variables || [];

  const handleAddVariable = () => {
    if (!newVarName.trim()) return;

    const id = newVarName.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    const variable: TemplateVariable = {
      id,
      name: newVarName.trim(),
      type: newVarType,
      defaultValue: newVarType === 'boolean' ? 'true' : '',
      required: false,
    };

    addVariable(variable);
    setNewVarName('');
    setIsAddingVariable(false);
  };

  const handleDeleteVariable = (varId: string) => {
    if (window.confirm('Delete this variable?')) {
      deleteVariable(varId);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="label mb-0">Template Variables</h3>
          <p className="text-xs text-fg-subtle mt-1">
            Variables can be bound to text layers using <code className="px-1 bg-white/10 rounded">{'{{variableName}}'}</code>
          </p>
        </div>
        <button
          onClick={() => setIsAddingVariable(true)}
          className="flex items-center gap-1 text-xs text-fg-muted hover:text-primary transition-colors"
        >
          <Plus size={14} />
          Add Variable
        </button>
      </div>

      {/* Add variable form */}
      {isAddingVariable && (
        <div className="p-3 bg-white/5 rounded-lg border border-white/10 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label text-xs">Name</label>
              <input
                type="text"
                value={newVarName}
                onChange={(e) => setNewVarName(e.target.value)}
                placeholder="e.g., discount_text"
                className="input"
                autoFocus
              />
            </div>
            <div>
              <label className="label text-xs">Type</label>
              <select
                value={newVarType}
                onChange={(e) => setNewVarType(e.target.value as VariableType)}
                className="input"
              >
                <option value="text">Text</option>
                <option value="color">Color</option>
                <option value="boolean">Boolean</option>
                <option value="image">Image URL</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={handleAddVariable} className="btn-primary text-xs py-1.5 px-3">
              Add
            </button>
            <button
              onClick={() => {
                setIsAddingVariable(false);
                setNewVarName('');
              }}
              className="text-xs text-fg-muted hover:text-fg"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Variables list */}
      {variables.length === 0 ? (
        <div className="text-center text-fg-subtle py-8 text-sm">
          <p>No variables defined</p>
          <p className="text-xs mt-1">Add variables to create dynamic content</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          {variables.map((variable) => (
            <VariableEditor
              key={variable.id}
              variable={variable}
              value={variableValues[variable.id] ?? variable.defaultValue}
              onChange={(value) => setVariableValue(variable.id, value)}
              onDelete={() => handleDeleteVariable(variable.id)}
              isBuiltin={['headline', 'subhead', 'cta'].includes(variable.id)}
            />
          ))}
        </div>
      )}

      {/* Usage help */}
      <div className="mt-6 p-3 bg-white/[0.02] rounded-lg border border-white/5">
        <h4 className="text-xs font-medium text-fg-muted mb-2">How to use variables</h4>
        <ul className="text-xs text-fg-subtle space-y-1">
          <li>• Select a text layer and set its text to <code className="px-1 bg-white/10 rounded">{'{{variableName}}'}</code></li>
          <li>• The variable value will be displayed in the canvas</li>
          <li>• Built-in variables: headline, subhead, cta</li>
        </ul>
      </div>
    </div>
  );
}

interface VariableEditorProps {
  variable: TemplateVariable;
  value: string;
  onChange: (value: string) => void;
  onDelete: () => void;
  isBuiltin: boolean;
}

function VariableEditor({ variable, value, onChange, onDelete, isBuiltin }: VariableEditorProps) {
  const Icon = VARIABLE_ICONS[variable.type];

  return (
    <div className="p-3 bg-white/[0.03] rounded-lg border border-white/5 group">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon size={14} className="text-fg-muted" />
          <span className="text-sm font-medium text-fg">{variable.name}</span>
          {variable.required && (
            <span className="text-[9px] text-accent-red">Required</span>
          )}
          {isBuiltin && (
            <span className="text-[9px] px-1 py-0.5 bg-primary/20 text-primary rounded">Built-in</span>
          )}
        </div>
        {!isBuiltin && (
          <button
            onClick={onDelete}
            className="opacity-0 group-hover:opacity-100 p-1 text-fg-subtle hover:text-accent-red transition-opacity"
          >
            <Trash2 size={12} />
          </button>
        )}
      </div>

      <div className="text-[10px] text-fg-subtle mb-2 font-mono">
        {'{{' + variable.id + '}}'}
      </div>

      {variable.type === 'text' && (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={variable.description || `Enter ${variable.name.toLowerCase()}`}
          className="input text-sm resize-none"
          rows={variable.id === 'headline' || variable.id === 'subhead' ? 2 : 1}
        />
      )}

      {variable.type === 'color' && (
        <div className="flex gap-2">
          <input
            type="color"
            value={value || '#FFFFFF'}
            onChange={(e) => onChange(e.target.value)}
            className="w-10 h-8 rounded border border-white/10 cursor-pointer"
          />
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="#FFFFFF"
            className="input flex-1 text-sm"
          />
        </div>
      )}

      {variable.type === 'boolean' && (
        <button
          onClick={() => onChange(value === 'true' ? 'false' : 'true')}
          className={`relative w-10 h-5 rounded-full transition-colors ${
            value === 'true' ? 'bg-primary' : 'bg-white/15'
          }`}
        >
          <div
            className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
              value === 'true' ? 'left-5' : 'left-0.5'
            }`}
          />
        </button>
      )}

      {variable.type === 'image' && (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Image URL"
          className="input text-sm"
        />
      )}
    </div>
  );
}
